import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { STATUS_LABEL, describeRetry, loadNotices, nextStep, parseUtc, resultLine, retryNotice } from '../lib/notices.js';
import { shortWhen } from '../lib/tone.js';

/* "Emails that did not go out" - on the queue, for staff.
 *
 * Shows nothing at all when there is nothing to show, and nothing while the server
 * feature is off (the API answers 404). When something is failed, given up or stuck,
 * it says so, why, and gives one button: Retry.
 *
 * THE ANSWER TO A RETRY LIVES IN ONE PANEL-LEVEL STATUS LINE, not in the row. A
 * successful retry removes the row from the list, and a message inside the row would
 * vanish with it - the one outcome the panel exists for would be silent. The status
 * line stays, takes focus (the pressed button is gone), and is read out by a screen
 * reader.
 */

function Icon({ name }) {
  return <span className="material-symbols-outlined" aria-hidden="true">{name}</span>;
}

export default function NoticePanel() {
  const [data, setData] = useState({ state: 'off', items: [] });
  const [busyId, setBusyId] = useState(null);
  const [status, setStatus] = useState(null); // { tone, text } - the last Retry's answer
  const statusRef = useRef(null);

  const refresh = useCallback(async () => setData(await loadNotices()), []);
  useEffect(() => { refresh(); }, [refresh]);

  async function retry(item) {
    setBusyId(item.eventId);
    try {
      const result = await retryNotice(item.eventId);
      setStatus({ ...describeRetry(result), text: resultLine(item.ticketNo, result) });
    } catch (err) {
      setStatus({ tone: 'bad', text: `${item.ticketNo}: ${err.message}` });
    } finally {
      setBusyId(null);
      await refresh();
      if (statusRef.current) statusRef.current.focus();
    }
  }

  if (data.state === 'off') return null;
  if (data.state === 'error') {
    return (
      <p className="muted" role="status" style={{ fontSize: 12 }}>
        Could not check for emails that did not go out. Reload to try again.
      </p>
    );
  }
  if (data.items.length === 0 && !status) return null;

  const statusLine = (
    <div ref={statusRef} role="status" aria-live="polite" tabIndex={-1} style={{ outline: 'none', margin: '6px 0' }}>
      {status ? <small className={status.tone === 'bad' ? 'muted' : undefined}><b>{status.text}</b></small> : null}
    </div>
  );

  return (
    <section className="panel" aria-label="Emails that did not go out" style={{ marginBottom: 16 }}>
      <h2 style={{ fontSize: 16, margin: '0 0 4px' }}>
        <Icon name="mail_off" />{' '}
        {data.items.length === 0
          ? 'No emails are waiting'
          : `${data.items.length} ${data.items.length === 1 ? 'email' : 'emails'} did not go out`}
      </h2>
      {data.items.length > 0 ? (
        <p className="muted" style={{ fontSize: 12, margin: '0 0 10px' }}>
          The ticket and the reply are saved. Only the email notice is waiting.
        </p>
      ) : null}
      {statusLine}
      {data.items.map((i) => {
        const created = parseUtc(i.createdAt);
        const to = Array.isArray(i.to) ? i.to : [];
        return (
          <div key={i.eventId} className="qrow" style={{ display: 'block', padding: '10px 0' }}>
            <div>
              <b>{STATUS_LABEL[i.status] || i.status}</b>{' · '}
              <Link to={`/ticket/${i.ticketNo}`}>{i.ticketNo}</Link>
              {i.subject ? ` · ${i.subject}` : ''}
              {created ? ` · saved ${shortWhen(created.toISOString())}` : ''}
            </div>
            <small className="muted">
              To {to.length ? to.join(', ') : 'nobody listed'} · tried {i.attempts} {i.attempts === 1 ? 'time' : 'times'}
              {i.lastError ? ` · Why: ${i.lastError}` : ''}
            </small>
            <div style={{ marginTop: 6 }}>
              <small>{nextStep(i)}</small>{' '}
              <button
                type="button"
                className="bulk__btn"
                disabled={busyId === i.eventId}
                aria-label={`Retry the email for ${i.ticketNo}`}
                onClick={() => retry(i)}
              >
                {busyId === i.eventId ? 'Sending…' : 'Retry'}
              </button>
            </div>
          </div>
        );
      })}
    </section>
  );
}
